/**
 * Account credit ("the wallet") API client - the user's own balance + ledger
 * (`GET /api/account/credit`, USER-scoped, no X-Site-Id needed).
 *
 * Credit is earned from the referral programme (Invite friends), from the signup
 * bonus, or granted by the host. It is spendable on one-off purchases (templates,
 * widgets, header/footer designs) in the builder OR on the plan/add-on invoice.
 * Backend: apps/api/src/modules/credit (config + grants), billing/lib/credit.ts (engine).
 */
import { useCallback, useEffect, useState } from "react";
import { api } from "./api";

export interface CreditLedgerRow {
  id: string;
  /** signup_welcome | referral_reward | referral_welcome | adjustment | release | spend_purchase | spend_stripe */
  kind: string;
  /** Negative = spending. */
  amountCents: number;
  spentCents: number;
  /** pending | vested | consumed | spent | released | pushed | voided | expired */
  status: string;
  createdAt: string;
  vestedAt: string | null;
  expiresAt: string | null;
  note: string | null;
}

export interface AccountCredit {
  accountId: string;
  accountName: string;
  /** Immediately spendable balance. */
  availableCents: number;
  /** Unvested rewards (waiting out the hold). */
  pendingCents: number;
  currency: string;
  nextExpiryAt: string | null;
  rows: CreditLedgerRow[];
}

export const credit = {
  summary: () => api.get<AccountCredit>("/account/credit"),
};

/**
 * The wallet for the whole account hub. `null` means "do not show a wallet at
 * all": either the module is off (404 credit_disabled) or the user has no account
 * (404 no_account). A zero balance is NOT null - it is shown, so the client can
 * see the wallet exists and how to fill it.
 */
export function useCredit(): { credit: AccountCredit | null; reload: () => void } {
  const [state, setState] = useState<AccountCredit | null>(null);

  const reload = useCallback(() => {
    credit.summary().then(setState).catch(() => setState(null));
  }, []);

  useEffect(() => reload(), [reload]);

  return { credit: state, reload };
}
